import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/lib/LanguageContext";
import { API } from "@/lib/config";

const STORAGE_KEY = "vardhan.consult.shown";
const DELAY_MS = 30_000;

export default function ConsultationModal() {
    const { t, lang, chosen } = useLang();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "" });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        // Only start the timer once the language has been chosen (i.e. user is
        // actually engaging with the page)
        if (!chosen) return;
        try {
            if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
        } catch (_) {
            /* ignore */
        }
        const id = setTimeout(() => setOpen(true), DELAY_MS);
        return () => clearTimeout(id);
    }, [chosen]);

    const close = () => {
        setOpen(false);
        try {
            sessionStorage.setItem(STORAGE_KEY, "1");
        } catch (_) {
            /* ignore */
        }
    };

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        if (!form.name.trim()) return t("consult.errors.name");
        if (!/^[0-9+\-\s]{6,}$/.test(form.phone.trim()))
            return t("consult.errors.phone");
        return null;
    };

    const submit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/consultations`, {
                name: form.name,
                phone: form.phone,
                language: lang,
            });
            setSuccess(data);
            toast.success(t("consult.toast_success"));
            try {
                sessionStorage.setItem(STORAGE_KEY, "1");
            } catch (_) {
                /* ignore */
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || t("consult.errors.server"),
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="fixed inset-0 z-[70] bg-[#2D2824]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                    data-testid="consultation-modal"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) close();
                    }}
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                        className="relative bg-[#F9F6F0] border border-[#B48F57] w-full max-w-lg p-6 md:p-10"
                    >
                        <button
                            onClick={close}
                            aria-label={t("consult.close")}
                            className="absolute top-3 right-3 md:top-4 md:right-4 w-9 h-9 border border-[#D9D1C7] hover:border-[#2D2824] hover:bg-[#2D2824] hover:text-[#F9F6F0] transition-colors flex items-center justify-center"
                            data-testid="consult-close"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                            >
                                <path d="M6 6l12 12M18 6L6 18" />
                            </svg>
                        </button>

                        {success ? (
                            <div data-testid="consult-success">
                                <div className="chapter-num mb-4">— {t("consult.eyebrow")}</div>
                                <h3 className="font-serif text-3xl md:text-4xl text-[#2D2824] tracking-tight leading-tight">
                                    {t("consult.success_title", { name: success.name })}
                                </h3>
                                <p className="mt-4 text-[14px] md:text-[15px] text-[#5C564D] leading-relaxed font-light">
                                    {t("consult.success_body", { phone: success.phone })}
                                </p>
                                <div className="mt-8">
                                    <button
                                        onClick={close}
                                        className="btn btn-dark"
                                        data-testid="consult-success-close"
                                    >
                                        {t("consult.close")}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="consult-form" noValidate>
                                <div className="chapter-num mb-3">— {t("consult.eyebrow")}</div>
                                <h3 className="font-serif text-3xl md:text-4xl text-[#2D2824] tracking-[-0.01em] leading-tight">
                                    {t("consult.title")}
                                </h3>
                                <p className="mt-4 text-[14px] md:text-[15px] text-[#5C564D] leading-relaxed font-light max-w-[42ch]">
                                    {t("consult.body")}
                                </p>

                                <div className="mt-8 grid grid-cols-1 gap-y-2">
                                    <div>
                                        <label className="eyebrow text-[#8A837A]">
                                            {t("consult.name")}
                                        </label>
                                        <input
                                            className="field"
                                            placeholder={t("consult.name_ph")}
                                            value={form.name}
                                            onChange={(e) => update("name", e.target.value)}
                                            data-testid="consult-input-name"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="eyebrow text-[#8A837A]">
                                            {t("consult.phone")}
                                        </label>
                                        <input
                                            className="field"
                                            placeholder={t("consult.phone_ph")}
                                            value={form.phone}
                                            onChange={(e) => update("phone", e.target.value)}
                                            data-testid="consult-input-phone"
                                            required
                                            type="tel"
                                        />
                                    </div>
                                </div>

                                <div className="mt-8 flex flex-wrap items-center gap-3">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn btn-dark disabled:opacity-60 disabled:cursor-not-allowed"
                                        data-testid="consult-submit"
                                    >
                                        {loading ? t("consult.submitting") : t("consult.submit")}
                                        <span aria-hidden>→</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={close}
                                        className="text-[12px] tracking-[0.18em] uppercase text-[#8A837A] hover:text-[#2D2824] transition-colors px-2 py-3"
                                        data-testid="consult-dismiss"
                                    >
                                        {t("consult.dismiss")}
                                    </button>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
